import React, { useState, useEffect } from 'react';
import { useTranslation }  from 'react-i18next';
import { RootState, useAppSelector, useAppDispatch, loadGroups, loadUsers } from '../../../redux';
import { Routine, RoutineAction, Group, User } from '../../../models';
import { Icon } from '../../Icon';
import { MultiSelect } from '../../MultiSelect';
import { FieldHelp } from '../../FieldHelp';

interface NotifyFormProps {
    action: RoutineAction;
    onSave?: (action: RoutineAction) => void;
    authorId?: string;
    projectId?: string;
    changeId?: string;
    defaultTitle?: string;
    defaultMessage?: string;
}

function NotifyForm(props: NotifyFormProps) {
    const { action, onSave, defaultTitle, defaultMessage } = props;

    const { i18n, t } = useTranslation();
    const dispatch    = useAppDispatch();

    const [options, setOptions] = useState<AnyObj>({});

    const { users }  = useAppSelector((state: RootState) => state.users);
    const { groups } = useAppSelector((state: RootState) => state.groups);

    useEffect(() => {
        dispatch(loadUsers());
        dispatch(loadGroups());
    }, [dispatch]);

    useEffect(() => {
        const next = { ...(action.options || {}) };
        if (defaultTitle && !next.title)
            next.title = defaultTitle;
        if (defaultMessage && !next.message)
            next.message = defaultMessage;
        setOptions(next);
    }, []);

    useEffect(() => {
        onSave && onSave({
            ...action,
            options
        });
    }, [options]);

    const usersOptions = users?.filter((a: User) => !!a.phone)
        .sort((a: User, b: User) => a.fullname().localeCompare(b.fullname()))
        .map((user: User) => ({
            value: user.phone,
            label: user.fullname(),
            leftContent: <Icon type="user" color="gray-600" className="ml-3 inline"/>
        }));

    const groupsOptions = groups?.filter((a: Group) => !!a.name)
        .sort((a: Group, b: Group) => a.name.localeCompare(b.name))
        .map((group: Group) => ({
            value: group._id,
            label: group.name,
            leftContent: <Icon type="signal" color="blue-600" className="ml-3 inline"/>
        }));

    /**
     *  "recipients": [ "string" ],
     *  "group_recipients": [ "string" ],
     *  "message": "string",
     *  "title": "string"
     */
    return (
        <div className="flex flex-col space-y-4 w-full">
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-2 bg-purple-50 dark:bg-purple-950/30 p-2 border rounded">
                <label className="text-sm col-span-1 flex-col items-center">
                    <div>{t('projects.routine.action.notify_recipients')}</div>
                    <small>{t('projects.routine.action.notify_recipients_desc')}</small>
                </label>
                <div className="col-span-3">
                    {options && (
                        <MultiSelect
                            defaultValue={options.recipients || []}
                            options={usersOptions}
                            onChange={(recipients: any) => { setOptions({...options, recipients}); }}
                            className="border rounded border-gray-300 dark:border-gray-600 w-full"
                            placeholder={t('projects.routine.action.notify_choose_recipients') || ''}
                        />
                    )}
                </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-2 bg-purple-50 dark:bg-purple-950/30 p-2 border rounded">
                <label className="text-sm col-span-1 flex-col items-center">
                    <div>{t('projects.routine.action.notify_group_recipients')}</div>
                    <small>{t('projects.routine.action.notify_group_recipients_desc')}</small>
                </label>
                <div className="col-span-3">
                    {options && (
                        <MultiSelect
                            defaultValue={options?.group_recipients || ''}
                            options={groupsOptions}
                            onChange={(group_recipients: any) => setOptions({...options, group_recipients})}
                            className="border rounded border-gray-300 dark:border-gray-600 w-full"
                            placeholder={t('projects.routine.action.notify_choose_group_recipients') || ''}
                        />
                    )}
                </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-2 bg-purple-50 dark:bg-purple-950/30 p-2 border rounded">
                <label className="text-sm col-span-1 flex-col items-center">
                    <div>{t('projects.routine.action.notify_language')}</div>
                    <small>{t('projects.routine.action.notify_language_desc')}</small>
                </label>
                <div className="col-span-3">
                    <select
                        value={options?.language || ''}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            const language = e.target.value || undefined;
                            const next = { ...options };
                            if (language)
                                next.language = language;
                            else
                                delete next.language;
                            setOptions(next);
                        }}
                        className="border rounded border-gray-300 dark:border-gray-600 w-full text-sm py-1.5"
                    >
                        <option value="">{t('projects.routine.action.notify_language_auto')}</option>
                        {['fr', 'en', 'de'].map((lang) => (
                            <option key={lang} value={lang}>
                                {t(`common.languages.${lang}`)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-2 bg-purple-50 dark:bg-purple-950/30 p-2 border rounded">
                <label className="text-sm col-span-1 flex-col items-center">
                    <div className="inline-flex items-center gap-1">
                        {t('projects.routine.action.notify_message')}
                        <FieldHelp content={t('projects.help.routine_shortcodes') || ''} />
                    </div>
                    <small>
                        {t(
                            action.type === RoutineAction.ACTION_NOTIFY_UNCLAIMED_CHANGES
                                ? 'projects.routine.action.notify_unclaimed_message_desc'
                                : 'projects.routine.action.notify_message_desc'
                        )}
                    </small>
                </label>
                <div className="col-span-3">
                    <textarea
                        value={options?.message || ''}
                        rows={3}
                        onChange={(e: any) => setOptions({...options, message: e.target.value})}
                        className="border rounded border-gray-300 dark:border-gray-600 w-full text-sm"
                    >
                        {options?.message}
                    </textarea>
                </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-2 bg-purple-50 dark:bg-purple-950/30 p-2 border rounded">
                <label className="text-sm col-span-1 flex-col items-center">
                    <div>{t('projects.routine.action.notify_title')}</div>
                    <small>{t('projects.routine.action.notify_title_desc')}</small>
                </label>
                <div className="col-span-3">
                    <textarea
                        value={options?.title || ''}
                        rows={3}
                        onChange={(e: any) => setOptions({...options, title: e.target.value})}
                        className="border rounded border-gray-300 dark:border-gray-600 w-full text-sm"
                    >
                        {options?.title}
                    </textarea>
                </div>
            </div>
        </div>
    );
}

export default NotifyForm;
